import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface User {
  id: number
  name: string
  email: string
  roles: string[]
  active_role: string
  member?: boolean
  rep?: boolean
  admin?: boolean
  superadmin?: boolean
  tenant_admin?: boolean
  impersonating?: boolean
}

export interface TenantConfig {
  hunts_enabled: boolean
  rifles_enabled: boolean
  courses_enabled: boolean
  products_enabled: boolean
  allow_purchases: boolean
  is_store_only: boolean
  terms_of_service_url: string
  show_login_register: boolean
  show_tenant_settings: boolean
  show_level_three: boolean
}

interface UserState {
  user: User | null
  tenant: TenantConfig | null
  loading: boolean
  error: string | null
  cartItemCount: number

  setUser: (user: User | null) => void
  setTenant: (tenant: TenantConfig | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setCartItemCount: (count: number) => void

  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  hasAllRoles: (roles: string[]) => boolean
  isAdmin: () => boolean
  isSuperadmin: () => boolean
  isRep: () => boolean
  isMember: () => boolean
  isAuthenticated: () => boolean

  fetchUserData: () => Promise<void>
  fetchCartCount: () => Promise<void>
}

export const useUserStore = create<UserState>()(
  devtools(
    (set, get) => ({
      user: null,
      tenant: null,
      loading: false,
      error: null,
      cartItemCount: 0,

      setUser: (user) => set({ user }),
      setTenant: (tenant) => set({ tenant }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setCartItemCount: (count) => set({ cartItemCount: count }),

      hasRole: (role) => {
        const { user } = get()
        if (!user) return false
        return user.roles.includes(role)
      },

      hasAnyRole: (roles) => {
        const { user } = get()
        if (!user) return false
        return roles.some(role => user.roles.includes(role))
      },

      hasAllRoles: (roles) => {
        const { user } = get()
        if (!user) return false
        return roles.every(role => user.roles.includes(role))
      },

      isAdmin: () => {
        const { user } = get()
        if (!user) return false
        return user.admin === true || user.superadmin === true || user.tenant_admin === true
      },

      isSuperadmin: () => {
        const { user } = get()
        if (!user) return false
        return user.superadmin === true
      },

      isRep: () => {
        const { user } = get()
        if (!user) return false
        return user.rep === true
      },

      isMember: () => {
        const { user } = get()
        if (!user) return false
        return user.member === true
      },

      isAuthenticated: () => {
        const { user } = get()
        return user !== null
      },

      fetchUserData: async () => {
        set({ loading: true, error: null })
        try {
          const response = await fetch('/api/current_user')
          if (!response.ok) {
            if (response.status === 401) {
              set({ user: null, loading: false })
              return
            }
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const data = await response.json()
          set({ 
            user: data.user, 
            tenant: data.tenant,
            loading: false, 
            error: null 
          })
        }
        catch (error) {
          console.error('Error fetching user data:', error)
          set({
            error: error instanceof Error ? error.message : 'An error occurred',
            loading: false,
          })
        }
      },

      fetchCartCount: async () => {
        try {
          const response = await fetch('/api/shopping_cart/count')
          if (!response.ok) {
            console.warn('Could not fetch cart count')
            return
          }
          const data = await response.json()
          set({ cartItemCount: data.count || 0 })
        }
        catch (error) {
          console.error('Error fetching cart count:', error)
        }
      },
    }),
    {
      name: 'user-store',
    },
  ),
)